import {
    all,
    call,
    delay,
    fork,
    put,
    select,
    takeLatest
} from 'redux-saga/effects';
import pick from 'lodash.pick';
import keyBy from 'lodash.keyby';
import { AxiosResponse, AxiosError } from 'axios';
// @ts-ignore
import { REHYDRATE } from 'redux-persist';

// Global Types
import { SagaIterator } from 'redux-saga';

// Global Constants
// @ts-ignore
import { SET_IS_ONLINE } from 'services/connection/constants';

// Global Utils
// @ts-ignore
import axiosPoll from 'utils/axiosPoll';
import { takeLatestWithCancel, putRequest } from 'utils/redux-saga';

// Global Selectors
// @ts-ignore
import { selectIsOnline } from 'services/connection/selectors';

// Local Types
import { MyTimesheetEntry } from './types';

// Local Actions
import {
    succeededLoadMyTimesheetEntries,
    failedLoadMyTimesheetEntries,
    requestSaveMyTimesheetEntries,
    succeededSaveMyTimesheetEntries,
    failedSaveMyTimesheetEntries,
    setReadOnlyMyTimesheetEntries,
    setMyTimesheetEntries,
    mergeSetMyTimesheetEntries,
    deleteMyTimesheetEntries
} from './actions';

// Local Selectors
import { selectMyTimesheetsLoadParams } from './params';
import {
    selectUnsavedMyTimesheetEntriesExist,
    selectUnsavedMyTimesheetEntries
} from './selectors';

// Local Constants
import {
    MY_TIMESHEET_ENTRIES_LOAD_REQUESTED,
    MY_TIMESHEET_ENTRIES_LOAD_CANCELED,
    MY_TIMESHEET_ENTRIES_SAVE_REQUESTED,
    MY_TIMESHEET_ENTRY_ADD,
    MY_TIMESHEET_ENTRY_UPDATE,
    MY_TIMESHEET_ENTRY_REMOVE,
    myTimesheetEntryFields
} from './constants';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* loadMyTimesheetsSucceeded(
    response: AxiosResponse
): SagaIterator {
    const { data } = response;
    const approvedTimesheetEntries = data.filter(
        (timesheetEntry: MyTimesheetEntry) =>
            timesheetEntry.lastReviewIsApproved
    );
    const unapprovedTimesheetEntries = data.filter(
        (timesheetEntry: MyTimesheetEntry) =>
            !timesheetEntry.lastReviewIsApproved
    );

    const approvedTimesheetEntriesKeyedByUuid = keyBy(
        approvedTimesheetEntries,
        'uuid'
    );
    const unapprovedTimesheetEntriesKeyedByUuid = keyBy(
        unapprovedTimesheetEntries,
        'uuid'
    );

    // Check for unsaved changes
    const unsavedMyTimesheetEntriesExist = yield select(
        selectUnsavedMyTimesheetEntriesExist
    );

    // Prevent unsaved changes from being removed
    if (!unsavedMyTimesheetEntriesExist) {
        yield put(setMyTimesheetEntries(unapprovedTimesheetEntriesKeyedByUuid));
    }

    yield put(
        setReadOnlyMyTimesheetEntries(approvedTimesheetEntriesKeyedByUuid)
    );

    yield put(succeededLoadMyTimesheetEntries(response));
}

export function* loadMyTimesheetFailed(error: AxiosError): SagaIterator {
    yield put(failedLoadMyTimesheetEntries(error));
}

export function* loadMyTimesheets(): SagaIterator {
    const params = yield select(selectMyTimesheetsLoadParams);

    yield call(axiosPoll, {
        config: {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/TimesheetEntries/me`,
            params
        },
        succeeded: loadMyTimesheetsSucceeded,
        failed: loadMyTimesheetFailed
    });
}

export function* watchMyTimesheetsLoadRequested(): SagaIterator {
    yield takeLatestWithCancel(
        MY_TIMESHEET_ENTRIES_LOAD_REQUESTED,
        MY_TIMESHEET_ENTRIES_LOAD_CANCELED,
        loadMyTimesheets
    );
}

export function* saveTimesheetEntries(): SagaIterator {
    yield delay(1000);

    const unsavedMyTimesheetEntries = yield select(
        selectUnsavedMyTimesheetEntries
    );

    try {
        const response = yield call(
            putRequest,
            'TimesheetEntries/bulk/me',
            unsavedMyTimesheetEntries
        );

        yield put(succeededSaveMyTimesheetEntries(response));

        const deletedTimesheetEntries = response.data.delete;
        const createdTimesheetEntries = response.data.create;
        const updatedTimesheetEntries = response.data.update;

        if (deletedTimesheetEntries) {
            const deletedTimesheetEntryUuids = deletedTimesheetEntries.map(
                (value: MyTimesheetEntry) => value.uuid
            );

            yield put(deleteMyTimesheetEntries(deletedTimesheetEntryUuids));
        }

        if (createdTimesheetEntries) {
            const createdTimesheetEntriesKeyedByUuid = keyBy(
                createdTimesheetEntries.map((timesheetEntry: any) =>
                    pick(timesheetEntry, myTimesheetEntryFields)
                ),
                'uuid'
            );

            yield put(
                mergeSetMyTimesheetEntries(createdTimesheetEntriesKeyedByUuid)
            );
        }

        if (updatedTimesheetEntries) {
            const updatedTimesheetEntriesKeyedByUuid = keyBy(
                updatedTimesheetEntries.map((timesheetEntry: any) =>
                    pick(timesheetEntry, myTimesheetEntryFields)
                ),
                'uuid'
            );

            yield put(
                mergeSetMyTimesheetEntries(updatedTimesheetEntriesKeyedByUuid)
            );
        }
    } catch (error) {
        yield put(failedSaveMyTimesheetEntries(error));
    }
}

export function* watchMyTimesheetsSaveRequested(): SagaIterator {
    yield takeLatest(MY_TIMESHEET_ENTRIES_SAVE_REQUESTED, saveTimesheetEntries);
}

export function* triggerSaveTimesheetEntries(): SagaIterator {
    const unsavedMyTimesheetEntries = yield select(
        selectUnsavedMyTimesheetEntries
    );
    const isOnline = yield select(selectIsOnline);

    if (unsavedMyTimesheetEntries && isOnline) {
        yield put(requestSaveMyTimesheetEntries());
    }
}

/**
 * watch for any actions that we want to trigger a save
 */
export function* watchMyTimesheetsSaveTriggers(): SagaIterator {
    yield takeLatest(
        [
            REHYDRATE,
            MY_TIMESHEET_ENTRY_ADD,
            MY_TIMESHEET_ENTRY_UPDATE,
            MY_TIMESHEET_ENTRY_REMOVE,
            SET_IS_ONLINE
        ],
        triggerSaveTimesheetEntries
    );
}

export default function* myTimesheetEntriesSagas(): SagaIterator {
    yield all([
        fork(watchMyTimesheetsLoadRequested),
        fork(watchMyTimesheetsSaveRequested),
        fork(watchMyTimesheetsSaveTriggers)
    ]);
}
